import { useMemo, useState, type CSSProperties } from 'react';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import {
  formatWorkspaceMetricValue,
} from '@/components/workspace/WorkspaceMetricCell';
import {
  WorkspaceOrderedHeaders,
  WorkspaceOrderedMetricCells,
} from '@/components/workspace/WorkspaceOrderedHeaders';
import {
  FilterSearch,
  FilterSelect,
  SelectionCheckbox,
  WorkspaceCheckboxCell,
  WorkspaceMiddleCell,
  ToolbarRow,
  WorkspaceSection,
  WorkspaceTablePagination,
  WorkspaceTableShell,
  tableRowClass,
  workspaceTableClass,
} from '@/components/workspace/WorkspaceSection';
import { WorkspaceTableSelectionActions } from '@/components/workspace/WorkspaceTableSelectionActions';
import { WorkspaceProductIdentityCell } from '@/components/workspace/WorkspaceProductIdentityCell';
import type { WorkspaceDimensionSelectionProps } from '@/components/workspace/workspaceSelectionTypes';
import type { WorkspaceMetricColumnDef } from '@/utils/workspaceAdProductMetrics';
import { ReportColumnPicker } from '@/components/ReportColumnPicker';
import { WorkspaceExportButton } from '@/components/workspace/WorkspaceExportButton';
import { WorkspaceMetricFiltersPopover } from '@/components/workspace/WorkspaceMetricFiltersPopover';
import { useMetricFilters } from '@/components/workspace/useMetricFilters';
import { useColumnOrder } from '@/components/workspace/useColumnOrder';
import { useWorkspaceFrozenLayout } from '@/components/workspace/workspaceStickyColumns';
import { useTableSort } from '@/components/workspace/useTableSort';
import { useServerPagination } from '@/hooks/useServerPagination';
import { useWorkspaceAdvertisedProductsPage } from '@/hooks/useWorkspaceProductAdsPages';
import type { EntityCrossFilter } from '@/utils/entityCrossFilter';
import {
  ADVERTISED_PRODUCT_ENTITY_COLUMNS,
  defaultColumnIds,
  workspaceColumnPickerOptions,
} from '@/components/workspace/workspaceColumnConfig';
import {
  applyWorkspaceSpProductAdStateChange,
  isSupportedProductAd,
  productStatusActionOptions,
  summarizeWorkspaceProductAdStateChange,
} from '@/api/workspaceBulkActions';
import { WorkspaceStateChangeModal } from '@/components/workspace/WorkspaceBulkActionModals';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import {
  exportWorkspaceTableCsv,
  fetchAllWorkspaceProductRows,
  workspaceExportColumns,
} from '@/api/workspaceCsvExport';

const AD_METRIC_COLUMNS: WorkspaceMetricColumnDef[] = [
  { key: 'cost', label: 'Ad Spend', polarity: 'lower_is_better', format: 'currency' },
  { key: 'cpc', label: 'CPC', polarity: 'lower_is_better', format: 'currency' },
  { key: 'ctr', label: 'CTR', polarity: 'higher_is_better', format: 'percent' },
  { key: 'cr', label: 'Ad CR%', polarity: 'higher_is_better', format: 'percent' },
  { key: 'purchases30d', label: 'Ad Orders', polarity: 'higher_is_better', format: 'int' },
  { key: 'impressions', label: 'Impressions', polarity: 'higher_is_better', format: 'int' },
  { key: 'clicks', label: 'Clicks', polarity: 'higher_is_better', format: 'int' },
  { key: 'sales30d', label: 'Ad Sales', polarity: 'higher_is_better', format: 'currency' },
  { key: 'acos', label: 'ACoS', polarity: 'lower_is_better', format: 'percent' },
  { key: 'roas', label: 'ROAS', polarity: 'higher_is_better', format: 'ratio' },
  { key: 'asp', label: 'ASP', polarity: 'higher_is_better', format: 'currency' },
  { key: 'totalSales', label: 'Total Sales', polarity: 'higher_is_better', format: 'currency' },
  { key: 'organicSales', label: 'Organic Sales', polarity: 'higher_is_better', format: 'currency' },
  { key: 'tacos', label: 'TACoS', polarity: 'lower_is_better', format: 'percent' },
];

const ADVERTISED_PRODUCT_COLUMN_IDS = defaultColumnIds(
  ADVERTISED_PRODUCT_ENTITY_COLUMNS,
  AD_METRIC_COLUMNS
);

/** Retail columns stay hidden by default until retail data pipeline ships. */
const ADVERTISED_PRODUCT_RETAIL_COLUMN_IDS = new Set([
  'inventory',
  'totalSales',
  'organicSales',
  'tacos',
]);

const ADVERTISED_PRODUCT_DEFAULT_VISIBLE_IDS = ADVERTISED_PRODUCT_COLUMN_IDS.filter(
  (id) => !ADVERTISED_PRODUCT_RETAIL_COLUMN_IDS.has(id)
);

function compareValues(left: string | number, right: string | number, dir: 'asc' | 'desc') {
  const result =
    typeof left === 'number' && typeof right === 'number'
      ? left - right
      : String(left).localeCompare(String(right), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
  return dir === 'asc' ? result : -result;
}

function NullableMetricCell({
  label,
  value,
  isLoading,
  className,
  style,
}: {
  label: string;
  value: number | null;
  isLoading: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  if (isLoading) {
    return (
      <td className={`${workspaceTableClass.td} px-4 ${className ?? ''}`} style={style}>
        <span className="ml-auto block h-4 w-12 animate-pulse rounded bg-surface-muted" />
      </td>
    );
  }
  return (
    <td
      className={`${workspaceTableClass.td} text-right font-mono tabular-nums text-[11px] text-foreground ${className ?? ''}`}
      style={style}
    >
      {formatWorkspaceMetricValue(
        { key: label, label, polarity: 'higher_is_better', format: 'currency' },
        value
      )}
    </td>
  );
}

export function WorkspaceAdvertisedProductTable({
  profileId,
  appliedFilter,
  enabled,
  rangeSubtitle,
  isFiltered,
  selection,
}: {
  profileId: string;
  appliedFilter: EntityCrossFilter;
  enabled: boolean;
  rangeSubtitle: string;
  isFiltered: boolean;
  selection: WorkspaceDimensionSelectionProps;
}) {
  const { showToast } = useToast();
  const { range } = useAppDateRange();
  const exportStart = range.current.startDate;
  const exportEnd = range.current.endDate;
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ENABLED');
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusApplying, setStatusApplying] = useState(false);
  const tableSort = useTableSort({ defaultSortBy: 'productName' });
  const metricFilters = useMetricFilters({
    tableId: 'advertised-products',
    metrics: AD_METRIC_COLUMNS,
  });
  const columnOrder = useColumnOrder(
    ADVERTISED_PRODUCT_COLUMN_IDS,
    ADVERTISED_PRODUCT_DEFAULT_VISIBLE_IDS
  );
  const freezeLayout = useWorkspaceFrozenLayout(
    columnOrder.frozenColumnIds,
    columnOrder.displayOrder.join('\0')
  );

  const resetKey = [
    search,
    statusFilter,
    JSON.stringify(appliedFilter),
    tableSort.sortBy,
    tableSort.sortDir,
    metricFilters.serialized,
    exportStart,
    exportEnd,
  ].join('|');

  const { page, pageSize, setPage, setPageSize } = useServerPagination(resetKey);

  const pageResult = useWorkspaceAdvertisedProductsPage({
    profileId,
    appliedFilter,
    page,
    pageSize,
    search,
    status: statusFilter,
    sortBy: tableSort.sortBy,
    sortDir: tableSort.sortDir,
    startDate: exportStart,
    endDate: exportEnd,
    metricFilters: metricFilters.payload,
    enabled,
  });

  const visible = useMemo(() => {
    const baseRows = pageResult.rows;
    return [...baseRows].sort((left, right) => {
      switch (tableSort.sortBy) {
        case 'productName':
          return compareValues(left.productName || left.asin, right.productName || right.asin, tableSort.sortDir);
        case 'status':
          return compareValues(left.status, right.status, tableSort.sortDir);
        case 'inventory':
          return compareValues(left.inventory ?? -1, right.inventory ?? -1, tableSort.sortDir);
        case 'totalSales':
          return compareValues(left.totalSales ?? -1, right.totalSales ?? -1, tableSort.sortDir);
        case 'organicSales':
          return compareValues(left.organicSales ?? -1, right.organicSales ?? -1, tableSort.sortDir);
        case 'tacos':
          return compareValues(left.tacos ?? -1, right.tacos ?? -1, tableSort.sortDir);
        default:
          return compareValues(
            left.adMetrics[tableSort.sortBy as keyof typeof left.adMetrics] ?? 0,
            right.adMetrics[tableSort.sortBy as keyof typeof right.adMetrics] ?? 0,
            tableSort.sortDir
          );
      }
    });
  }, [pageResult.rows, tableSort.sortBy, tableSort.sortDir]);

  const visibleIds = visible.map((r) => r.asin);
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selection.pendingIds.includes(id));
  const subtitleParts = [`${pageResult.totalCount} products`, rangeSubtitle];
  if (isFiltered) subtitleParts.push('Filtered to selected entities');
  const isTableLoading = pageResult.isPending;

  const handleActionSelect = (actionId: string) => {
    if (actionId === 'change_status') setStatusModalOpen(true);
  };

  const handleConfirmStatusChange = async (state: 'ENABLED' | 'PAUSED') => {
    const selectedAsins = new Set(selection.pendingIds.map((id) => id.toUpperCase()));
    const supportedRows = new Map(
      pageResult.catalog
        .filter((row) => selectedAsins.has(row.asin.toUpperCase()) && isSupportedProductAd(row.adProduct))
        .map((row) => [row.adId, { adId: row.adId, label: row.productName || row.asin || row.adId }])
    );
    const supported = [...supportedRows.values()];
    const skippedCount = Math.max(selection.pendingIds.length - supported.length, 0);
    if (!supported.length) {
      showToast('Only SP advertised products are supported for status updates right now.', 'error');
      return;
    }
    setStatusApplying(true);
    try {
      await applyWorkspaceSpProductAdStateChange(profileId, supported, state);
      showToast(
        summarizeWorkspaceProductAdStateChange(
          supported.length,
          skippedCount,
          state,
          'advertised product'
        ),
        skippedCount > 0 ? 'error' : 'success'
      );
      setStatusModalOpen(false);
      selection.onClearSelection();
      pageResult.refetch();
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to update advertised product status',
        'error'
      );
    } finally {
      setStatusApplying(false);
    }
  };

  return (
    <WorkspaceSection
      title="Advertised Product Level"
      countLabel={subtitleParts.join(' · ')}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <WorkspaceMetricFiltersPopover metrics={AD_METRIC_COLUMNS} {...metricFilters} />
          <WorkspaceExportButton
            onExport={async () => {
              try {
                const fetched = await fetchAllWorkspaceProductRows({
                  profileId,
                  kind: 'advertised-products',
                  appliedFilter: appliedFilter as unknown as Record<string, unknown>,
                  state: statusFilter !== 'ALL' ? statusFilter : undefined,
                  search: search.trim() || undefined,
                  sortBy: tableSort.sortBy,
                  sortDir: tableSort.sortDir,
                  startDate: exportStart,
                  endDate: exportEnd,
                  metricFilters: metricFilters.payload,
                });
                const columns = workspaceExportColumns(
                  columnOrder.visibleIds,
                  ADVERTISED_PRODUCT_ENTITY_COLUMNS,
                  AD_METRIC_COLUMNS
                );
                const rows = fetched.rows.map((row) => {
                  const adMetrics =
                    row.adMetrics && typeof row.adMetrics === 'object'
                      ? (row.adMetrics as Record<string, unknown>)
                      : {};
                  return {
                    productName: row.productName ?? row.asin ?? '',
                    status: row.status ?? '',
                    inventory: row.inventory ?? '',
                    totalSales: row.totalSales ?? '',
                    organicSales: row.organicSales ?? '',
                    tacos: row.tacos ?? '',
                    metrics: {
                      ...adMetrics,
                      totalSales: row.totalSales,
                      organicSales: row.organicSales,
                      tacos: row.tacos,
                    },
                  };
                });
                exportWorkspaceTableCsv({
                  filenamePrefix: 'workspace_advertised_products',
                  startDate: exportStart,
                  endDate: exportEnd,
                  columns,
                  rows,
                });
                if (fetched.truncated) {
                  showToast(
                    `Exported first ${fetched.rows.length.toLocaleString()} of ${fetched.totalCount.toLocaleString()} rows`,
                    'error'
                  );
                } else {
                  showToast(`Exported ${fetched.rows.length.toLocaleString()} rows`, 'success');
                }
              } catch (error) {
                showToast(
                  error instanceof Error ? error.message : 'Failed to export advertised products',
                  'error'
                );
              }
            }}
          />
          <ReportColumnPicker
            columns={workspaceColumnPickerOptions(ADVERTISED_PRODUCT_ENTITY_COLUMNS, AD_METRIC_COLUMNS)}
            selected={columnOrder.visibleIds}
            onChange={columnOrder.setVisibleIds}
            requiredColumnId="productName"
            panelTitle="Visible columns"
          />
        </div>
      }
    >
      <ToolbarRow>
        <div className="flex flex-wrap items-end gap-3">
          <FilterSearch
            id="workspace-advertised-product-search"
            label="Products"
            value={search}
            onChange={setSearch}
          />
          <FilterSelect
            id="workspace-advertised-product-status"
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
          >
            <option value="ALL">All</option>
            <option value="ENABLED">Enabled</option>
            <option value="PAUSED">Paused</option>
            <option value="ARCHIVED">Archived</option>
          </FilterSelect>
          <WorkspaceTableSelectionActions
            entityLabel="ASIN"
            selectionCount={selection.selectionCount}
            hasPendingChanges={selection.hasPendingChanges}
            canApply={selection.canApply}
            onApply={selection.onApply}
            onClearSelection={selection.onClearSelection}
            disabled={isTableLoading}
            actions={productStatusActionOptions()}
            onActionSelect={handleActionSelect}
          />
        </div>
      </ToolbarRow>
      <p className="mb-2 text-xs text-foreground-muted">
        Ad metrics await product-ad report ingest. Inventory, Total Sales, Organic Sales, and TACoS
        require retail data (shown as —).
      </p>
      <WorkspaceTableShell
        isEmpty={!pageResult.isPending && visible.length === 0}
        isLoading={isTableLoading}
        emptyMessage={
          isFiltered
            ? 'No advertised products match the current selection.'
            : 'No product ads for this account.'
        }
        footer={
          <WorkspaceTablePagination
            page={page}
            pageSize={pageSize}
            total={pageResult.totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            disabled={isTableLoading}
          />
        }
      >
        <table ref={freezeLayout.tableRef} className={workspaceTableClass.table}>
          <thead>
            <tr>
              <WorkspaceCheckboxCell isHeader>
                <SelectionCheckbox
                  checked={allSelected}
                  label="Select all visible products"
                  disabled={isTableLoading}
                  onChange={(checked) => selection.onToggleAll(visibleIds, checked)}
                />
              </WorkspaceCheckboxCell>
              <WorkspaceOrderedHeaders
                order={columnOrder.displayOrder}
                entityColumns={ADVERTISED_PRODUCT_ENTITY_COLUMNS}
                metricColumns={AD_METRIC_COLUMNS}
                sortBy={tableSort.sortBy}
                sortDir={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                onFreeze={columnOrder.freezeColumn}
                frozenColumnIds={columnOrder.frozenColumnIds}
                pinnedFrozenColumnId={columnOrder.pinnedFrozenColumnId}
                frozenLeftById={freezeLayout.frozenLeftById}
                onDragStartColumn={columnOrder.startDrag}
                onDragHoverColumn={columnOrder.hoverReorder}
                onDropColumn={columnOrder.commitDrop}
                onDragEndColumn={columnOrder.endDrag}
                activeDragColumnId={columnOrder.activeDragColumnId}
                hoverColumnId={columnOrder.hoverColumnId}
                hoverPosition={columnOrder.hoverPosition}
                hoverInsertIndex={columnOrder.hoverInsertIndex}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visible.map((row) => {
              const selected = selection.pendingIds.includes(row.asin);
              return (
                <tr key={row.asin} className={tableRowClass(selected)}>
                  <WorkspaceCheckboxCell>
                    <SelectionCheckbox
                      checked={selected}
                      label={`Select product ${row.asin}`}
                      disabled={isTableLoading}
                      onChange={(checked) => selection.onToggleId(row.asin, checked)}
                    />
                  </WorkspaceCheckboxCell>
                  <WorkspaceOrderedMetricCells
                    order={columnOrder.displayOrder}
                    entityColumns={ADVERTISED_PRODUCT_ENTITY_COLUMNS}
                    frozenColumnIds={columnOrder.frozenColumnIds}
                    frozenLeftById={freezeLayout.frozenLeftById}
                    metrics={
                      {
                        ...row.adMetrics,
                        totalSales: row.totalSales,
                        organicSales: row.organicSales,
                        tacos: row.tacos,
                      } as Record<string, number>
                    }
                    previousMetrics={null}
                    compareEnabled={false}
                    isLoading={isTableLoading}
                    metricColumns={AD_METRIC_COLUMNS}
                    activeDragColumnId={columnOrder.activeDragColumnId}
                    hoverColumnId={columnOrder.hoverColumnId}
                    hoverPosition={columnOrder.hoverPosition}
                    renderEntityCell={(columnId, meta) => {
                      if (columnId === 'productName') {
                        return (
                          <WorkspaceProductIdentityCell
                            productName={row.productName}
                            asin={row.asin}
                            imageUrl={row.imageUrl}
                            className={meta.className}
                            style={meta.style}
                          />
                        );
                      }
                      if (columnId === 'status') {
                        return (
                          <WorkspaceMiddleCell
                            style={meta.style}
                            className={`app-table-cell-wrap whitespace-normal ${meta.className}`}
                            alignToTitle
                          >
                            <Badge variant="neutral" className="h-auto max-w-[14rem] whitespace-normal text-left">
                              {row.status}
                            </Badge>
                          </WorkspaceMiddleCell>
                        );
                      }
                      if (columnId === 'inventory') {
                        return (
                          <NullableMetricCell
                            label="inventory"
                            value={row.inventory}
                            isLoading={isTableLoading}
                            className={meta.className}
                            style={meta.style}
                          />
                        );
                      }
                      return null;
                    }}
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      </WorkspaceTableShell>
      {statusModalOpen ? (
        <WorkspaceStateChangeModal
          title="Change Advertised Product Status"
          entityLabel="advertised product"
          selectionCount={selection.pendingIds.length}
          pending={statusApplying}
          onCancel={() => setStatusModalOpen(false)}
          onConfirm={(state) => void handleConfirmStatusChange(state)}
        />
      ) : null}
    </WorkspaceSection>
  );
}
